<template>
  <div class="cws-container">
    <b-row>
      <b-col cols="12">
        <b-card title="Проверка пакета Шазамом" border-variant="primary">
          <b-card-text>
            Загрузите <strong>один .siq</strong> — каждый аудиофайл вопросов проверяется по первым 5 сек
            и сверяется с ответом. Или <strong>несколько mp3</strong> — берутся 5 сек из середины,
            ответ для сверки — имя файла («Артист - Название»). Один запрос RapidAPI на файл.
          </b-card-text>

          <b-form-group label="Пакет (.siq) или mp3-файлы" label-for="shazam-check-file">
            <b-form-file
                id="shazam-check-file"
                v-model="selectedFiles"
                accept=".siq,.mp3"
                multiple
                placeholder="Выберите .siq или mp3..."
                browse-text="Обзор"
                :disabled="isProcessing"
            />
            <small v-if="selectionError" class="text-danger">{{ selectionError }}</small>
          </b-form-group>

          <b-button variant="primary" :disabled="!canRun || isProcessing" @click="runCheck">
            <b-spinner v-if="isProcessing" small class="mr-1"/>
            Проверить
          </b-button>

          <div v-if="isProcessing || statusText" class="mt-3">
            <b-progress :max="progressMax" height="1.2rem">
              <b-progress-bar :value="progressValue" :variant="progressVariant">
                {{ progressLabel }}
              </b-progress-bar>
            </b-progress>
            <small class="text-muted">{{ statusText }}</small>
          </div>

          <div v-if="items.length" class="mt-2">
            <b-badge
                v-for="status in statusOrder"
                :key="status"
                :variant="statusMeta[status].variant"
                class="mr-50"
            >
              {{ statusMeta[status].label }}: {{ counts[status] }}
            </b-badge>
            <span class="text-muted small ml-50">из {{ items.length }}</span>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-if="items.length">
      <b-col cols="12">
        <b-card no-body>
          <b-table :items="items" :fields="fields" small responsive :tbody-tr-class="rowClass" class="mb-0">
            <template #cell(place)="{ item }">
              <template v-if="item.round !== null">
                <div class="small text-muted">{{ item.round }}</div>
                <div>{{ item.theme }} · {{ item.price }}</div>
              </template>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(answer)="{ item }">
              <div>{{ item.answer }}</div>
              <div class="small text-muted">{{ item.filename }}</div>
            </template>
            <template #cell(recognized)="{ item }">
              <div v-if="item.recognized" class="d-flex align-items-center">
                <b-img v-if="item.recognized.cover" :src="item.recognized.cover" width="36" height="36" rounded class="mr-50"/>
                <a v-if="item.recognized.url" :href="item.recognized.url" target="_blank" rel="noopener">
                  {{ item.recognized.artist }} — {{ item.recognized.title }}
                </a>
                <span v-else>{{ item.recognized.artist }} — {{ item.recognized.title }}</span>
              </div>
              <span v-else :class="item.status === 'error' ? 'text-warning' : 'text-muted'">{{ item.summary }}</span>
            </template>
            <template #cell(status)="{ item }">
              <b-badge :variant="statusMeta[item.status].variant">{{ statusMeta[item.status].label }}</b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BButton, BSpinner,
  BProgress, BProgressBar, BTable, BBadge, BImg,
} from 'bootstrap-vue'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'

// Для пакета хорошо, когда Шазам песню НЕ узнаёт.
const statusMeta = {
  match: { label: 'Шазамится', variant: 'danger', rowClass: 'table-danger' },
  other: { label: 'Другой трек', variant: 'warning', rowClass: 'table-warning' },
  not_recognized: { label: 'Не распознан', variant: 'success', rowClass: '' },
  error: { label: 'Ошибка', variant: 'secondary', rowClass: '' },
}
const statusOrder = ['match', 'other', 'not_recognized', 'error']
const MAX_MP3_FILES = 20

export default {
  components: {
    BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BButton, BSpinner,
    BProgress, BProgressBar, BTable, BBadge, BImg,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const selectedFiles = ref([])
    const isProcessing = ref(false)
    const statusText = ref('')
    const progressValue = ref(0)
    const progressMax = ref(1)
    const progressVariant = ref('primary')
    const items = ref([])

    const fields = [
      { key: 'place', label: 'Раунд / тема' },
      { key: 'answer', label: 'Ответ (файл)' },
      { key: 'recognized', label: 'Шазам' },
      { key: 'status', label: 'Статус' },
    ]

    const extensionOf = selected => (selected.name.split('.').pop() || '').toLowerCase()
    const siqFiles = computed(() => (selectedFiles.value || []).filter(selected => extensionOf(selected) === 'siq'))
    const mp3Files = computed(() => (selectedFiles.value || []).filter(selected => extensionOf(selected) === 'mp3'))

    // Режим пакета — ровно один .siq без mp3; режим файлов — только mp3.
    const selectionError = computed(() => {
      if (siqFiles.value.length > 1) return 'Можно проверить только один .siq за раз'
      if (siqFiles.value.length && mp3Files.value.length) return '.siq и mp3 вместе не проверяются — выберите что-то одно'
      // PHP max_file_uploads = 20: лишние файлы молча отбрасываются сервером.
      if (mp3Files.value.length > MAX_MP3_FILES) return `Не больше ${MAX_MP3_FILES} mp3 за раз (выбрано ${mp3Files.value.length})`
      return ''
    })
    const canRun = computed(() => !selectionError.value && (siqFiles.value.length + mp3Files.value.length) > 0)

    const counts = computed(() => {
      const result = { match: 0, other: 0, not_recognized: 0, error: 0 }
      items.value.forEach(item => { result[item.status] += 1 })
      return result
    })

    const progressLabel = computed(() => {
      if (progressMax.value <= 1 && !progressValue.value) return statusText.value ? '...' : ''
      return `${progressValue.value} / ${progressMax.value}`
    })

    const rowClass = item => (item ? statusMeta[item.status].rowClass : '')

    const onProgress = payload => {
      if (payload.event === 'start') {
        progressMax.value = payload.total || 1
        progressValue.value = 0
        statusText.value = payload.total ? `Аудиофайлов: ${payload.total}` : 'Нет аудиофайлов для проверки'
      } else if (payload.event === 'item') {
        progressValue.value = payload.index + 1
        statusText.value = `Проверено: ${payload.item.filename}`
        items.value.push(payload.item)
      } else if (payload.event === 'done') {
        progressValue.value = progressMax.value
        progressVariant.value = 'success'
        statusText.value = 'Готово'
      }
    }

    const runCheck = async () => {
      if (!canRun.value) return
      isProcessing.value = true
      items.value = []
      statusText.value = 'Загрузка пакета...'
      progressValue.value = 0
      progressMax.value = 1
      progressVariant.value = 'primary'

      try {
        await store.dispatch('playlist/shazamCheckPack', siqFiles.value.length
          ? { file: siqFiles.value[0], onProgress }
          : { files: mp3Files.value, onProgress })
      } catch (error) {
        progressVariant.value = 'danger'
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка проверки Шазамом', text: error.message, icon: 'AlertTriangleIcon', variant: 'danger',
          },
        })
      } finally {
        isProcessing.value = false
      }
    }

    return {
      selectedFiles, selectionError, canRun, isProcessing, statusText, items, fields, counts,
      progressValue, progressMax, progressVariant, progressLabel,
      statusMeta, statusOrder, rowClass, runCheck,
    }
  },
}
</script>
